import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Typography, Paper, Grid, Stack, Button, Chip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, useTheme, Alert, InputAdornment, Divider, List, ListItem, ListItemText
} from "@mui/material";
import {
  RefreshOutlined, EditOutlined, CreditCardOutlined,
  CheckCircleOutlined, ErrorOutline, TimerOutlined,
  NotificationsActiveOutlined, SearchOutlined, FilterListOutlined,
  HistoryToggleOffOutlined, AccountBalanceWalletOutlined,
  TrendingUpOutlined, ArrowBackIosNewOutlined, CalendarMonthOutlined,
  FiberManualRecord
} from "@mui/icons-material";
import { getAllSubscriptions, updateSubscriptionStatus, runAutoChecks } from "../../store/slices/subscriptionSlice";
import { toast } from "react-toastify";

import { motion, AnimatePresence } from "framer-motion";

const AdminSubscriptions = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { subscriptions, loading } = useSelector(state => state.subscription);
  const isDark = theme.palette.mode === 'dark';

  const [editDialog, setEditDialog] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newDate, setNewDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(getAllSubscriptions());
  }, [dispatch]);

  const handleUpdate = async () => {
    try {
      await dispatch(updateSubscriptionStatus({
        subId: editDialog._id,
        status: newStatus,
        endDate: newDate
      })).unwrap();
      toast.success("Subscription updated");
      setEditDialog(null);
      dispatch(getAllSubscriptions());
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleRunChecks = async () => {
    try {
      const res = await dispatch(runAutoChecks()).unwrap();
      toast.success(res.message);
      dispatch(getAllSubscriptions());
    } catch (err) {
      toast.error("Failed to run checks");
    }
  };

  const statusColors = {
    ACTIVE: '#56ca00',
    TRIAL: '#6366f1',
    SUSPENDED: '#8b5cf6',
    EXPIRED: '#ff4c51',
    CANCELLED: '#8a8d93'
  };

  const filteredSubscriptions = subscriptions.filter(s => 
    s.partnerId?.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.partnerId?.businessDetails?.theaterDisplayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        {/* Header section with Stats */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 6 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: "-0.04em" }}>Economic Hub</Typography>
            <Typography variant="body1" sx={{ opacity: 0.6, fontWeight: 500 }}>Orchestrating partner liquidity and membership lifecycles</Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Synchronize Subscription States">
               <IconButton onClick={() => dispatch(getAllSubscriptions())} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                 <RefreshOutlined />
               </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<TimerOutlined />}
              onClick={handleRunChecks}
              sx={{ borderRadius: '12px', px: 3, py: 1.2, textTransform: 'none', fontWeight: 800, bgcolor: '#6366f1', boxShadow: '0 8px 20px rgba(99,102,241,0.3)' }}
            >
              Run Global Compliance Check
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {[
            { label: 'Active Members', value: subscriptions.filter(s => s.status === 'ACTIVE').length, icon: <CheckCircleOutlined />, color: '#56ca00' },
            { label: 'Trial Pipelines', value: subscriptions.filter(s => s.status === 'TRIAL').length, icon: <TimerOutlined />, color: '#6366f1' },
            { label: 'Revenue Pool', value: `₹${(subscriptions.filter(s => s.status === 'ACTIVE').reduce((acc, s) => acc + (s.price || 0), 0)).toLocaleString()}`, icon: <AccountBalanceWalletOutlined />, color: '#ffb400' },
            { label: 'Critical Assets', value: subscriptions.filter(s => ['EXPIRED', 'SUSPENDED'].includes(s.status)).length, icon: <ErrorOutline />, color: '#ff4c51' },
          ].map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Paper elevation={0} sx={{
                p: 3, borderRadius: '24px',
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`,
                display: 'flex', alignItems: 'center', gap: 2.5
              }}>
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: `${stat.color}15`, color: stat.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {stat.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, lineHeight: 1.1 }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, opacity: 0.4, textTransform: 'uppercase', mt: 0.5, letterSpacing: '1px' }}>{stat.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Table & Filtering */}
        <Paper elevation={0} sx={{
          p: 0, borderRadius: '24px', overflow: 'hidden',
          bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`
        }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
             <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search partner or theater..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchOutlined sx={{ fontSize: 20, opacity: 0.5 }} />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: '12px', bgcolor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)' }
                    }}
                  />
                </Grid>
                <Grid item sx={{ ml: 'auto' }}>
                   <Button variant="outlined" startIcon={<FilterListOutlined />} sx={{ borderRadius: '10px', textTransform: 'none' }}>Filters</Button>
                </Grid>
             </Grid>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 800, opacity: 0.5, fontSize: '0.72rem', textTransform: 'uppercase', py: 2.5, px: 3, letterSpacing: '1px' } }}>
                  <TableCell>Partner Profile</TableCell>
                  <TableCell>Active Plan</TableCell>
                  <TableCell>Lifecycle Status</TableCell>
                  <TableCell>Expiry Timeline</TableCell>
                  <TableCell>Economic Value</TableCell>
                  <TableCell align="right">Orchestration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubscriptions.map((sub) => (
                  <TableRow key={sub._id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' } }}>
                    <TableCell sx={{ px: 3 }}>
                      <Stack direction="row" spacing={1.8} alignItems="center">
                        <Avatar sx={{ width: 42, height: 42, bgcolor: theme.palette.primary.main, fontWeight: 800 }}>
                          {sub.partnerId?.userName?.[0] || 'P'}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>{sub.partnerId?.userName || 'Deleted Partner'}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', opacity: 0.6 }}>{sub.partnerId?.businessDetails?.theaterDisplayName || 'Unknown Entity'}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={sub.plan} size="small" sx={{ fontWeight: 800, height: 22, fontSize: '0.68rem', borderRadius: '6px' }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecord sx={{ fontSize: 10, color: statusColors[sub.status] }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: statusColors[sub.status] }}>{sub.status}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                          {sub.endDate ? new Date(sub.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '∞'}
                        </Typography>
                        {sub.endDate && new Date(sub.endDate) < new Date() && sub.status === 'ACTIVE' && (
                          <Chip label="OVERDUE" color="error" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 900 }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 900 }}>
                         {sub.paymentHistory?.length > 0
                          ? `₹${(sub.paymentHistory[sub.paymentHistory.length - 1].amount).toLocaleString()}`
                          : '₹0'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', opacity: 0.5 }}>Accumulated Value</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 3 }}>
                      <IconButton
                        onClick={() => {
                          setEditDialog(sub);
                          setNewStatus(sub.status);
                          setNewDate(sub.endDate ? sub.endDate.split('T')[0] : '');
                        }}
                        sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: '10px' }}
                      >
                        <EditOutlined sx={{ fontSize: 20 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} PaperProps={{ sx: { borderRadius: '28px', minWidth: 500, p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 900, fontSize: '1.4rem' }}>Protocol Override</DialogTitle>
          <DialogContent>
            <Stack spacing={4} sx={{ mt: 2 }}>
              <Box sx={{ p: 2, borderRadius: '16px', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 2 }}>
                 <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>{editDialog?.partnerId?.userName?.[0]}</Avatar>
                 <Box>
                    <Typography sx={{ fontWeight: 800 }}>{editDialog?.partnerId?.userName}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', opacity: 0.6 }}>{editDialog?.partnerId?.userEmail}</Typography>
                 </Box>
              </Box>
              
              <Grid container spacing={2}>
                 <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Override Status"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="TRIAL">TRIAL</option>
                      <option value="SUSPENDED">SUSPENDED</option>
                      <option value="EXPIRED">EXPIRED</option>
                      <option value="CANCELLED">CANCELLED</option>
                    </TextField>
                 </Grid>
                 <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Adjust Expiry"
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                 </Grid>
              </Grid>

              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, opacity: 0.5, mb: 1, textTransform: 'uppercase' }}>Financial Ledger</Typography>
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                   {(editDialog?.paymentHistory || []).slice(-5).reverse().map((pay, p) => (
                      <Paper key={p} variant="outlined" sx={{ p: 1.5, minWidth: 120, borderRadius: '12px', textAlign: 'center' }}>
                         <Typography sx={{ fontSize: '0.85rem', fontWeight: 900 }}>₹{pay.amount}</Typography>
                         <Typography sx={{ fontSize: '0.6rem', opacity: 0.6 }}>{new Date(pay.date).toLocaleDateString()}</Typography>
                         <Chip label={pay.status} size="small" color={pay.status === 'SUCCESS' ? 'success' : 'error'} sx={{ height: 14, fontSize: '0.55rem', fontWeight: 800, mt: 0.5 }} />
                      </Paper>
                   ))}
                </Stack>
              </Box>
              
              <Alert severity="warning" icon={<ErrorOutline />} sx={{ borderRadius: '16px' }}>
                Applying <b>SUSPENDED</b> or <b>EXPIRED</b> status will cut off theater nodes instantly. Proceed with caution.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setEditDialog(null)} sx={{ borderRadius: '14px', textTransform: 'none', px: 3 }}>Dismiss</Button>
            <Button variant="contained" onClick={handleUpdate} sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, bgcolor: '#6366f1', px: 4, py: 1.2 }}>Apply Manifest Update</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default AdminSubscriptions;
